import { useState, useRef } from 'react';
import { Camera } from 'lucide-react';
import { toast } from 'react-toastify';
import { showApiErrors } from '../../utils/errorHandler';
import { useAuth } from '../../context/Auth';

const ProfileHeader = ({ memberDetails, api_url, onProfilePictureUpdate }) => {
  const {requestWithAuth} = useAuth()
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await requestWithAuth(
        `/members/${memberDetails.id}/`,
         "PUT",
          formData,
          {
            "Content-Type": "multipart/form-data",
          },
        
      );

      
      
      // Call the callback function to update the parent component
      if (onProfilePictureUpdate) {
        onProfilePictureUpdate(response?.data?.profile_picture);
      }

      toast.success('Profile picture updated successfully!');
    } catch (error) {
      showApiErrors(error)
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex justify-between items-center py-2 px-5 border-b border-slate-300">
      <div className="flex items-center gap-2">
        <div className="relative h-10 w-10 rounded-full bg-blue-500 group">
          {memberDetails?.profile_picture ? (
            <img 
              src={`${api_url}${memberDetails?.profile_picture}`} 
              alt="Profile" 
              className="h-full w-full object-cover rounded-full" 
            />
          ) : (
            <div className="h-full w-full rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
              {memberDetails?.first_name?.charAt(0)}{memberDetails?.last_name?.charAt(0)}
            </div>
          )}
          
          {/* Edit Button Overlay */}
          <button
            onClick={handleEditClick}
            disabled={isUploading}
            className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 disabled:cursor-not-allowed"
            title="Change profile picture"
          >
            {isUploading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            ) : (
              <Camera className="h-4 w-4 text-white" />
            )}
          </button>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            onChange={handleFileChange}
            className="hidden"
            disabled={isUploading}
          />
        </div>
        
        <div className="leading-tight">
          <h2 className="font-semibold">
            {memberDetails?.first_name} {memberDetails?.last_name}
          </h2>
          <small className="text-gray-500">
            {memberDetails?.member_church_id}
          </small>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;